import { Input } from '@commenda/kakashi';
import { TEquityInfo } from '@commenda/types/src/incorporation/types';
import React from 'react';

type CompanyValuationInputProps = {
  equityInfo: Partial<TEquityInfo>;
  setEquityInfo: React.Dispatch<React.SetStateAction<TEquityInfo>>;
  shareCountPlaceholder: string;
  sharePricePlaceholder: string;
  currency?: {
    name: string;
    style: string;
    countryCode: string;
    decimalPlacesAllowed?: number;
  };
  helpTexts?: {
    mainHeading?: string;
    bodyText?: string;
    connectingText?: string;
  };
  readOnly?: boolean;
  isNumberOfSharesApplicable?: boolean;
  disabled?: boolean;
};

const regexThatMatchesNumberWithCommas = /^[0-9][0-9,]*$/;

const CompanyValuationInputKS = ({
  equityInfo,
  setEquityInfo,
  shareCountPlaceholder,
  sharePricePlaceholder,
  currency = {
    name: 'USD',
    style: '$',
    countryCode: 'en-US',
  },
  helpTexts,
  readOnly = false,
  isNumberOfSharesApplicable = true,
  disabled = false,
}: CompanyValuationInputProps) => {
  return (
    <div className="flex flex-col gap-6 border-b pb-12">
      <div>
        <h3 className="text-base">
          {helpTexts?.mainHeading || 'Authorized Shares'}
        </h3>
        <span className="text-sm text-neutral-2">
          {helpTexts?.bodyText ||
            'We suggest you to keep the following default values to minimize corporate taxes.'}
        </span>
      </div>
      <div className="flex flex-col gap-4">
        {isNumberOfSharesApplicable ? (
          <div className="flex items-center gap-6">
            <div className="flex flex-col gap-1 shrink-0 grow">
              <Input
                type="text"
                maxLength={15}
                readOnly={readOnly || disabled}
                placeholder={shareCountPlaceholder}
                value={equityInfo?.shareCount?.toLocaleString()}
                onChange={(e) => {
                  const newValue = e.currentTarget.value;
                  if (!newValue) {
                    setEquityInfo((current) => ({
                      ...current,
                      shareCount: undefined,
                    }));
                  } else if (regexThatMatchesNumberWithCommas.test(newValue)) {
                    setEquityInfo((current) => ({
                      ...current,
                      shareCount: parseInt(newValue.replaceAll(',', ''), 10),
                    }));
                  }
                }}
              />
            </div>
            <p className="body-15 shrink-0">
              {helpTexts?.connectingText || 'shares valued at'}
            </p>
            <div className="relative shrink-0 grow">
              <div className="flex flex-col gap-1">
                <Input
                  className="text-right hide-arrow pl-6"
                  type="number"
                  name="price"
                  id="price"
                  min={0}
                  readOnly={readOnly || disabled}
                  maxLength={9}
                  placeholder={sharePricePlaceholder}
                  value={equityInfo.price}
                  onChange={(e) => {
                    const currentValue = e.target.value;
                    if (currentValue.includes('.')) {
                      const [intPart, decimalPart] = currentValue.split('.');
                      if (decimalPart.length > currency?.decimalPlacesAllowed)
                        return;
                      const formattedIntPart =
                        intPart.replace(/^0+/, '') || '0';
                      e.target.value = `${formattedIntPart}.${decimalPart}`;
                    } else {
                      // For non-decimal numbers, remove leading zeros but allow a single zero
                      e.target.value = currentValue.replace(/^0+/, '') || '0';
                    }
                    const newValue = parseFloat(e.target.value);

                    if (
                      !Number.isNaN(newValue) &&
                      newValue >= 0 &&
                      newValue <= 100000000
                    ) {
                      setEquityInfo((current) => ({
                        ...current,
                        price: newValue,
                      }));
                    }
                  }}
                />
              </div>
              <span className="absolute top-1/2 -translate-y-1/2 left-3 select-none touch-none text-neutral-4">
                {currency.style}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CompanyValuationInputKS;
